/* eslint-disable no-useless-catch */
/* eslint-disable spaced-comment */
/* eslint-disable no-unused-vars */
/* eslint-disable class-methods-use-this */
import * as crypt from 'crypto';
import {ApolloClient, createHttpLink, split} from '@apollo/client';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import {WebSocketLink} from '@apollo/client/link/ws';
import { setContext } from '@apollo/client/link/context';
import { RetryLink } from '@apollo/client/link/retry';
import { createUploadLink } from 'apollo-upload-client';
import {getMainDefinition} from '@apollo/client/utilities';
import gql from 'graphql-tag';
import invariant from 'ts-invariant';
import {BatchHttpLink} from '@apollo/client/link/batch-http';
import {onError} from '@apollo/client/link/error';
import {InMemoryCache} from '@apollo/client/cache';
import {isBefore} from 'date-fns';
import { encode as base64encode } from 'base64-arraybuffer';
import { 
  saveSSOReturnUrl,
  clearMsalStorage,
  isUnrecoverableMsalSessionError
} from './app-shell-utils';
import { trackLogin, trackEvent, setUserProperties, trackError } from '../utils/gaTagManager';
import {intlLink, uniqueRequestIdLink} from './session-context';
import sessionContextStorage from './session-context/sessionStorage';
import {loginRequest, silentRequest} from '../msal/msalConfig';
import introspectionQueryResultData from '../apollo/instrospectionQueryResultData.json';

/**
 * Returns true if the given MSAL error requires the user to re-authenticate.
 * Covers: SPA refresh token expired (invalid_grant / AADSTS700084), silent iframe
 * timeout (monitor_window_timeout, caused by X-Frame-Options: deny), and all
 * interaction-required / login-required / consent-required error codes.
 */
const MSAL_EXPIRED_ERROR_CODES = [
  'monitor_window_timeout',
  'interaction_required',
  'login_required',
  'consent_required',
  'invalid_grant'
];

export const isMsalTokenExpiredError = (e) => {
  if (e instanceof InteractionRequiredAuthError) return true;
  const errorCode = String(e?.errorCode || e?.error || '');
  const subError = String(e?.subError || e?.suberror || '');
  const errorDescription = String(e?.errorDescription || e?.error_description || e?.message || '');
  return MSAL_EXPIRED_ERROR_CODES.some(code => errorCode.includes(code)) ||
    subError.includes('bad_token') ||
    errorDescription.includes('AADSTS700084');
};

const parseIso8601Date = (date) => {
  const d = date.split(/\D+/);
  return new Date(Date.UTC(d[0], d[1] - 1, d[2], d[3], d[4], d[5], d[6]));
}

const AUTH_TRACE = 'auth.trace.id';
const AUTH_REDIRECT_SYNC_TS = 'auth.redirect.sync.ts';
const AUTH_REDIRECT_GRACE_WINDOW_MS = 15000;
/**
* Public members and methods:
*
* msal: IPublicClientApplication MSAL PCA instance
* cip:                           CIP configuration
* contentful:                    Contentful configuration
* apiUrl: string
* apiWsUrl: string | undefined
* apolloClient: IApolloClient    This client will be already authenticated to talk to contentful, and our super graph apis
* setInternalUser(boolean)       Sets the user as internal or external
*/
export class Auth {
   constructor(props) {
    this.authFailureRedirectPath = props.authFailureRedirectPath
    this.genericApiFailureRedirectPath = props.genericApiFailureRedirectPath
    this.cacheKey = 'auth';
    this.cache = props.cache;
    this.apiUrl = props.apiUrl
    this.apiWsUrl = props.apiWsUrl
    this.cip = props.cip
    this.msal = props.msal
    this.contentful = props.contentful
    this.batch = props.batch
    this.debug = props.debug === true
    this.isLocal = props.isLocal === true
    this.mainWindowRedirectUri = props.mainWindowRedirectUri
    this.accessTokenValidityThresholdMs = props.accessTokenValidityThresholdMs || 10_000
    this.waitingForPongTimeoutMs = props.waitingForPongTimeoutMs || 5_000
    this.pingIntervalMs = props.pingIntervalMs || 2_000
    this.retryAttempts = props.retryAttempts || 100
    this.sessionContext = props.sessionContext
    this.apolloClient = props.apolloClient
    this.isCipMuleActive = props.isCipMuleActive
    this.logError = props.logError;
  }
 
  /**
   * PUBLIC
   */
  getApolloClient() {
    if (!this.apolloClient) {
      this.apolloClient = this.makeApolloClient()
    }
    return this.apolloClient
  }
 
  resetSession(alsoDeleteInternal) {
    const newSessionContext = alsoDeleteInternal ? {} : {isInternal: this.isUserInternal(), codeVerifier: this.codeVerifier};
    this.sessionContext.replace(newSessionContext)
    
    // 🔧 CRITICAL: Also clear sessionContext from localStorage on logout/session reset
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.removeItem('sessionContext');
        window.sessionStorage.removeItem('sessionContext');
      } catch (storageError) {
        // ignore storage failure
      }
    }
  }
 
  /**
   * @TODO: store and retrieve user info from outside the session or from a field in it
   * @returns {{ id, type, isInternal, roles, firstName, lastName, phone, username, email, localeInfo }}
   */
   getUserInfo() {
    const { id, type, isInternal, roles, firstName, lastName, phone, username, email, localeInfo } = this.getSessionParams()
    return {
      id,
      type,
      isInternal,
      roles,
      firstName,
      lastName,
      phone,
      username,
      email,
      localeInfo
    }
  }
 
  getUserRoles() {
    if (!this.isUserLoggedIn()) {
      return []
    }
    const {roles} = this.getUserInfo()
    return roles ?? []
  }
 
  getUserType() {
    if (!this.isUserLoggedIn()) {
      return undefined
    }
    const {type} = this.getUserInfo()
    return type
  }
 
  isUserLoggedIn() {
    const { type: userType, isInternal } = this.getUserInfo()
    const userSessionPresent = Boolean(userType && userType !== 'NONE' && userType !== 'NOROLES')
    return userSessionPresent && (!isInternal || !!this.msal.instance.getActiveAccount())
  }
 
  setUserInternal() {
    let persistedSession = {}
    if (typeof window !== 'undefined') {
      try {
        const raw = window.localStorage.getItem('sessionContext')
          || window.sessionStorage.getItem('sessionContext')
        if (raw) {
          persistedSession = JSON.parse(raw) || {}
        }
      } catch (e) {
        persistedSession = {}
      }
    }

    const currentSession = this.getSessionParams() || {}
    this.setSessionParams({
      ...persistedSession,
      ...currentSession,
      isInternal: true
    })
  }
 
  setUserExternal() {
    this.setSessionParams({isInternal: false})
  }
 
  isUserInternal() {
    const {isInternal} = this.getUserInfo()
    return isInternal
  }
 
  async ensureUserIsLoggedIn() {
    if (!this.isUserLoggedIn()) {
      if (this.isUserInternal()) {
        await this.getMsalAccessToken()
      } else {
        await this.login()
      }
    }
  }
 
  async generateLoginUrl() {
    // const { verifier, challenge } = await this.generatePKCECodes();
    // this.storeCodeVerifier(session, verifier);
    const authUrl = new URL(`${this.cip.baseUri}`);
    authUrl.searchParams.append('client_id', this.cip.redirectUri);
    authUrl.searchParams.append('code_challenge', 'S256');
    authUrl.searchParams.append('code_challenge_method', 'S256');
    authUrl.searchParams.append('redirect_uri', this.cip.redirectUri);
    authUrl.searchParams.append('response_type', 'code');
    authUrl.searchParams.append('scope', 'openid b2b-consent');
    return authUrl.toString();
  }
 
  async login() {
    // GTM: Track login event
    trackLogin(this.isUserInternal() ? 'MSAL' : 'CIP');
    
    if (this.isUserInternal()) {
      const interactionLock = Auth.getMsalInteractionLock()
      if (interactionLock) {
        const isStaleExpiredTokenLock = interactionLock.reason?.includes('expiredToken');
        if (!isStaleExpiredTokenLock) {
          return;
        }
        Auth.clearMsalInteractionLock();
      }

      Auth.setMsalInteractionLock('Auth.login.loginRedirect')
      await this.msal.instance.loginRedirect(loginRequest)
        .catch(e => {
          if (String(e?.errorCode || '').includes('interaction_in_progress')
            || String(e?.message || '').includes('interaction_in_progress')
            || String(e?.message || '').includes('msal_interaction_in_progress')) {
            return
          }
          if (String(e?.message || '').includes('state_not_found')) {
            Auth.clearMsalInteractionLock()
          }
          this.logError?.logErrorToServer?.({
            message: `SSO login redirect failed: ${e?.errorCode || e?.error || e?.message || 'unknown'}`,
            stackTrace: e?.stack || e?.error_description,
            component: 'Auth - login',
            type: 'error',
            statusCode: e?.status || 500
          });
          trackError('login_error', e.message);
        });
    } else {
    //  await this.generateCodeChallenge();
 
    //   window.location.href = "localhost:3000/auth/landing";
      // window.location.href = this.generateLoginUrl();//`${this.cip.baseUri}&code_challenge_method=S256&code_challenge=${this.muleCodeChallenge}&nonce=testingNonce&client_id=${this.cip.clientId}&redirect_uri=${this.cip.redirectUri}`
    }
     
  }
 
  async generateCodeChallenge() {
    this.codeVerifier = crypt.randomBytes(32).toString('hex');
    this.setSessionParams({...this.getSessionParams(), codeVerifier: this.codeVerifier});
    const encoder = new TextEncoder();
    const data = encoder.encode(this.codeVerifier);
    const digest = await crypto.subtle.digest('SHA-256', data);
    const base64Digest = base64encode(digest);
  
    this.muleCodeChallenge = base64Digest
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=/g, "");
  }
 
  /**
   * Completes the OAUTH code authorisation flow for external users
   * @param {string} code authorisation code as returned by CIP
   * @returns {Promise<void>}
   */
  async completeCIPAuthorisationFlow(code) {
    const self = this
    this.resetSession(true);
    await this.gqlAuthCallbackCIP(code)
      .then(async (result) => {
        const userInfo = await self.gqlUserInfo(true)
        self.setUserInfo(userInfo)
        
        // GTM: Set user properties after successful login
        setUserProperties({
          user_id: userInfo?.id,
          user_email: userInfo?.email,
          user_name: `${userInfo?.firstName} ${userInfo?.lastName}`,
          user_type: 'external'
        });
      })
      .catch((e) => {
        this.logError?.logErrorToServer?.({
          message: `CIP authorisation flow failed: ${e?.message || 'unknown'}`,
          stackTrace: e?.stack,
          component: 'Auth - completeCIPAuthorisationFlow',
          type: 'error',
          statusCode: e?.status || 500
        });
        trackError('cip_auth_error', e.message);
        throw e
      })
  }
 
  async logout() {
    // GTM: Track logout event
    const isInternal = this.isUserInternal();
    trackEvent('logout', {
      logout_method: isInternal ? 'MSAL' : 'CIP'
    });
    
    const redirectUrl = await this.getApolloClient().mutate({
      mutation: this.GQL_USER_LOGOUT,
      variables: { isInternal }
    }).then((logoutResult) => {
      invariant(logoutResult?.data?.authLogout, `failed graphql auth callback: ${JSON.stringify(logoutResult)}`)
      return logoutResult.data.authLogout
    }).catch((e) =>{
      this.logError?.logErrorToServer?.({
        message: `Logout failed: ${e?.message || 'unknown'}`,
        stackTrace: e?.stack,
        component: 'Auth - logout',
        type: 'error',
        statusCode: e?.status || 500
      });
      trackError('logout_error', e.message);
      return isInternal ? '/auth/employee' : '/auth/landing';
    })
    this.resetSession()
    if (isInternal) {
      const logoutRequest = {
        account: this.msal.instance.getActiveAccount(),
        mainWindowRedirectUri: redirectUrl,
        postLogoutRedirectUri: redirectUrl
      };
      await this.msal.instance.logoutRedirect(logoutRequest)
        .catch(e => {
          trackError('msal_logout_error', e.message);
        });
    } else {
      try {
        const safeLogoutUrl = new URL(redirectUrl, window.location.origin);
        if (!['http:', 'https:'].includes(safeLogoutUrl.protocol)) {
          throw new Error('Invalid protocol');
        }
        window.location.href = safeLogoutUrl.href;
      } catch (e) {
        window.location.href = '/';
      }
    }
  }
 
  /**
   * PRIVATE
   */
 
  GQL_AUTH_CALLBACK = gql`
      mutation AuthCallback($code: String, $source: String, $isInternal: Boolean, $isLocal: Boolean, $codeVerifier: String) {
          authCallback(code: $code, source: $source, isInternal: $isInternal, isLocal: $isLocal, codeVerifier: $codeVerifier) {
              wasSuccessful
          }
      }`
 
  GQL_ME = gql`
      query Me {
          me {
              id
              firstName
              lastName
              username
              email
              phone
              roles
              localeInfo {
                countryCode
                currency
                languageCode
              }
              organization {
                name
                id
              }
          }
      }`
 
  GQL_USER_LOGOUT = gql`
      mutation AuthLogout($isInternal: Boolean)  {
          authLogout(isInternal: $isInternal)
      }`;
 
  getSessionParams() {
    return this.sessionContext
  }
 
  setSessionParams(params) {
    this.sessionContext.set(params)
  }
 
  static setPendingMsalAccessToken(pendingToken) {
    window.pendingMsalAccessToken = pendingToken
  }
 
  static getPendingMsalAccessToken() {
    return window.pendingMsalAccessToken
  }

  static getAuthTraceId() {
    if (typeof window === 'undefined') return 'server'
    try {
      let traceId = window.sessionStorage.getItem(AUTH_TRACE)
      if (!traceId) {
        traceId = `auth-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
        window.sessionStorage.setItem(AUTH_TRACE, traceId)
      }
      return traceId
    } catch (e) {
      return 'auth-trace-unavailable'
    }
  }

  static getMsalInteractionLock() {
    if (typeof window === 'undefined') return null
    try {
      const raw = window.sessionStorage.getItem('msal.interaction.lock')
      if (!raw) return null
      const parsed = JSON.parse(raw)
      if (!parsed?.createdAt) return null
      if (Date.now() - parsed.createdAt > 60000) {
        window.sessionStorage.removeItem('msal.interaction.lock')
        return null
      }
      return parsed
    } catch (e) {
      return null
    }
  }

  static setMsalInteractionLock(reason = 'unknown') {
    if (typeof window === 'undefined') return
    try {
      window.sessionStorage.setItem('msal.interaction.lock', JSON.stringify({ reason, createdAt: Date.now() }))
    } catch (e) {
      // ignore storage failure
    }
  }

  static clearMsalInteractionLock() {
    if (typeof window === 'undefined') return
    try {
      window.sessionStorage.removeItem('msal.interaction.lock')
    } catch (e) {
      // ignore storage failure
    }
  }

  /**
   * Store redirect state to preserve user's page and customer context (static method)
   * Used when redirecting to SSO login (/auth/employee)
   */
  static storeRedirectState(state = {}) {
    try {
      const redirectState = {
        returnUrl: state.returnUrl || (typeof window !== 'undefined' ? window.location.pathname + window.location.search : '/'),
        customerContext: state.customerContext || null,
        timestamp: Date.now()
      }
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('auth.redirectState', JSON.stringify(redirectState))
      }
      return redirectState
    } catch (e) {
      return null
    }
  }

  /**
   * Retrieve previously stored redirect state (static method)
   */
  static getRedirectState() {
    try {
      if (typeof window !== 'undefined') {
        const stored = window.localStorage.getItem('auth.redirectState')
        if (stored) {
          const state = JSON.parse(stored)
          // Only return state if it's less than 10 minutes old
          if (Date.now() - state.timestamp < 600000) {
            return state
          }
          // Clear expired state
          window.localStorage.removeItem('auth.redirectState')
        }
      }
    } catch (e) {
      //Failed to retrieve redirect state: return null
    }
    return null
  }

  /**
   * Clear stored redirect state (static method)
   */
  static clearRedirectState() {
    try {
      if (typeof window !== 'undefined') {
        window.localStorage.removeItem('auth.redirectState')
      }
    } catch (e) {
      //Failed to clear redirect state
    }
  }

  /**
   * Initiates redirect to SSO login (/auth/employee) with state preservation
   * Called when silent token refresh fails
   */
  async redirectToSSOLogin(state = {}) {
    const authTraceId = Auth.getAuthTraceId()
    trackEvent('session_refresh_failure', { authTraceId, reason: 'redirecting_to_sso_login' }); 
    this.logError?.logErrorToServer?.({
      message: `Redirecting to SSO login. authTraceId: ${authTraceId}`,
      stackTrace: undefined,
      component: 'Auth - redirectToSSOLogin',
      type: 'warn',
      statusCode: 401
    });
    
    // Store current page and context before redirecting (use static method)
    // CRITICAL FIX: Don't store redirect state if we're already on an auth page (prevents loops)
    const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
    const authPagePaths = ['/auth/employee', '/auth/callback', '/auth/landing', '/auth/asmUser', '/auth/asmLogin'];
    const isAlreadyOnAuthPage = authPagePaths.some(path => currentPath.includes(path));
    const skipReturnUrl = !!state?.skipReturnUrl;
    
    if (!isAlreadyOnAuthPage && !skipReturnUrl) {
      Auth.storeRedirectState(state);
    } else {
      // Clear any existing redirect state 
      Auth.clearRedirectState();
    }
    
    clearMsalStorage();

    if (!isAlreadyOnAuthPage && !skipReturnUrl) {
      saveSSOReturnUrl();
    }
    
    // Allow a small delay for logging to complete
    return new Promise(resolve => {
      setTimeout(() => {
        // Redirect to SSO employee login page
        if (typeof window !== 'undefined') {
          window.location.href = '/auth/employee'
        }
        resolve()
      }, 100)
    })
  }

  cacheGet(key, defaultValue) {
    let cacheEntry = this.cache.getItem(`${this.cacheKey}.${key}`)
    if (!cacheEntry) {
      return defaultValue;
    }
    cacheEntry = JSON.parse(cacheEntry);
    const hasExpired = cacheEntry.expiresOn && isBefore(new Date(cacheEntry.expiresOn), Date.now());
    return (!cacheEntry || hasExpired) ? undefined : cacheEntry.value
  }
 
  cacheSet(key, value, ttl) {
    this.cache.setItem(
      `${this.cacheKey}.${key}`,
      JSON.stringify({expiresOn: (Date.now() + ttl ?? 2 * 3600 * 1000), value}))
  }
 
  makeApolloClient() {
    const self = this
    const retryDelayMs = 300;
    const maxOperationRetries = 5;
    const retriableOperations = ["InvoiceSearch", "AccountSearchResults", "OverdueInvoicesRes"];
 
    const retryLink = new RetryLink({
      delay: {
        initial: retryDelayMs,
        max: Infinity,
        jitter: true
      },
      attempts: (count, operation, error) => {
        if (count >= maxOperationRetries) {
          this.logError?.logErrorToServer?.({
            message: `GraphQL request failed after ${maxOperationRetries} retries.`,
            stackTrace: error?.stack,
            component: "Auth - makeApolloClient - retry",
            type: "error",
            statusCode: error.status || 500
          });
          return false;
        }
        
        // Check if this is an UNAUTHENTICATED error
        const isAuthError = error?.graphQLErrors?.some(err => 
          [err?.extensions?.exception?.code, err?.extensions?.code].includes('UNAUTHENTICATED')
        );
        
        // Retry if operation is in retriable list OR if it's an auth error
        const shouldRetry = isAuthError || retriableOperations.includes(operation.operationName);
        
        if (isAuthError) {
          // Clear the bad token so next retry gets a fresh one
          self.cache.removeItem(`${self.cacheKey}.msalLastAuthResult`)
        }

        if (shouldRetry) {
          this.logError?.logErrorToServer?.({
            message: `GraphQL request failed; retrying (${count}/${maxOperationRetries}).`,
            stackTrace: error?.stack,
            component: "Auth - makeApolloClient - retry",
            type: "warn",
            statusCode: error.status || 503
          });
        }

        return !!error && shouldRetry;
      }
    });
 
    const contentfulLink = createHttpLink({
      uri: `${this.contentful.contentfulBaseUrl}/spaces/${this.contentful.contentfulSpaceId}/environments/${this.contentful.contentfulEnvironmentAlias}`,
      headers: {
        authorization: `Bearer ${this.contentful.contentfulAccessToken}`
      }
    });
 
    const wsLink = this.apiWsUrl && new WebSocketLink({
      uri: process.env.GRAPHQL_WEBSOCKET,
      options: {
        reconnect: true
      }
    })
 
    const checkAccessToken = setContext(async (_, prevContext) => {
      const {headers = {}, skipAzureTokenCheck, ...otherContext} = prevContext
      if (!self.isUserInternal() || skipAzureTokenCheck) {
        // 🔧 FIX: Return full context object, not just headers
        return {
          ...otherContext,
          headers,
          skipAzureTokenCheck
        };
      }

      const msalAccessToken = await self.getMsalAccessToken();
      return {
        ...otherContext,
        headers: {
          ...headers,
          Authorization: `Bearer ${msalAccessToken}`
        },
        skipAzureTokenCheck
      }
    })
 
    const baseLink = this.batch ?
      new BatchHttpLink({uri: this.apiUrl, credentials: 'include'})
      : createUploadLink({
        uri: this.apiUrl,
        credentials: 'include'
      })
 
    const httpLink = split(
      operation => operation.getContext().isContentRequest,
      contentfulLink,
      uniqueRequestIdLink.concat(intlLink.concat(checkAccessToken.concat(retryLink).concat(baseLink)))
    );
    const cache = new InMemoryCache({
      possibleTypes: introspectionQueryResultData
    }).restore(
      // eslint-disable-next-line no-underscore-dangle
      typeof window === 'undefined' ? {} : window.__APOLLO_STATE__
      
    )
 
    const includingWsLink = wsLink ?
      split(
        ({query}) => {
          const {kind, operation} = getMainDefinition(query)
          return kind === 'OperationDefinition' && operation === 'subscription';
        },
        wsLink,
        baseLink
      )
      : httpLink;
 
    const errorHandler = onError((errorResponse) => {
      self.handleApolloErrors(errorResponse)
    })
    const link = errorHandler.concat(includingWsLink)
    return new ApolloClient({link, cache})
  }
 
  async handleApolloErrors(errorResponse) {
    const {graphQLErrors} = errorResponse
    const hasAuthError = graphQLErrors && !!graphQLErrors.find(err => [err?.extensions?.exception?.code, err?.extensions?.code].includes('UNAUTHENTICATED'))

    if (hasAuthError) {
      await this.handleAuthError(errorResponse)
    } else {
      this.handleNonAuthError(errorResponse)
    }
  }

  static isInternalAuthRoute() {
    const currentPath = typeof window !== 'undefined' ? window.location?.pathname || '' : ''
    return currentPath.includes('/auth/employee') || currentPath.includes('/auth/asmUser')
  }

  static isInPostRedirectGraceWindow(isInternalAuthRoute) {
    if (typeof window === 'undefined' || !isInternalAuthRoute) {
      return false
    }
    try {
      const lastRedirectSyncTs = Number(window.sessionStorage.getItem(AUTH_REDIRECT_SYNC_TS) || 0)
      const msSinceRedirectSync = lastRedirectSyncTs ? Date.now() - lastRedirectSyncTs : Number.POSITIVE_INFINITY
      return msSinceRedirectSync >= 0 && msSinceRedirectSync < AUTH_REDIRECT_GRACE_WINDOW_MS
    } catch (e) {
      return false
    }
  }

  redirectToUrl(targetUrl) {
    if (typeof window === 'undefined' || window.location.href.includes(this.authFailureRedirectPath)) {
      return
    }
    // This is to avoid loops in the auth error page itself, if any apis fail
    try {
      const safeUrl = new URL(targetUrl, window.location.origin);
      if (!['http:', 'https:'].includes(safeUrl.protocol)) {
        throw new Error('Invalid protocol');
      }
      window.location.href = safeUrl.href;
    } catch (e) {
      window.location.href = '/';
    }
  }

  async handleAuthError(errorResponse) {
    const {graphQLErrors, operation} = errorResponse
    this.logError?.logErrorToServer?.({
      message: `Apollo UNAUTHENTICATED error detected. Operation: ${operation?.operationName || 'unknown'}`,
      stackTrace: graphQLErrors?.map(err => err?.message).join('; '),
      component: 'Auth - handleApolloErrors',
      type: 'error',
      statusCode: 401
    });

    const isInternalAuthRoute = Auth.isInternalAuthRoute()
    const isInPostRedirectGraceWindow = Auth.isInPostRedirectGraceWindow(isInternalAuthRoute)

    const recovered = await this.tryRecoverBackendSession()
    if (recovered) {
      return
    }

    // Clear the cached token since it's invalid
    this.cache.removeItem(`${this.cacheKey}.msalLastAuthResult`)

    if (!operation.getContext().shouldFailUponAuthFailure) {
      // For MSAL (internal) users: clear cache and let retryLink retry with a fresh token
      // For external users: redirect to login
      if (!this.isUserInternal()) {
        await this.login()
      }
      return
    }

    if (isInternalAuthRoute && isInPostRedirectGraceWindow) {
      return
    }

    this.redirectToUrl(this.authFailureRedirectPath)
  }

  handleNonAuthError(errorResponse) {
    const {graphQLErrors, operation} = errorResponse

    if (graphQLErrors && Auth.shouldSkipRedirectOnRegistrationError(graphQLErrors)) {
      return;
    }

    if (operation.getContext().shouldAlwaysFailAsAuthError) {
      this.redirectToUrl(process.env.AAD_FAILURE_URI)
    }
  }
 
  static shouldSkipRedirectOnRegistrationError = (graphQLErrors) => {
    const hasRegistrationError = graphQLErrors.find(err => [err?.extensions?.exception?.code, err?.extensions?.code].includes('EXPIRED_TOKEN_ERROR')
    || [err?.extensions?.exception?.code, err?.extensions?.code].includes('CONFLICT_USER_ERROR'));
    return hasRegistrationError;
  }
 
  getMSALAuthResult() {
    return this.cacheGet('msalLastAuthResult')
  }

  async tryRecoverBackendSession() {
    if (typeof window === 'undefined') return false
    const recoveryTimestampId = 'auth.session.recovery.attempted.ts'
    const RECOVERY_WINDOW_MS = 30000

    try {
      const lastTs = Number(window.sessionStorage.getItem(recoveryTimestampId) || 0)
      if (lastTs && Date.now() - lastTs < RECOVERY_WINDOW_MS) return false
    } catch (e) {
      // sessionStorage unavailable — fall through and attempt anyway
    }

    const cached = this.getMSALAuthResult()
    const accessToken = cached?.accessToken
    
    const rawExpiresOn = cached?.expiresOn
    let expiresOnMs = 0
    if (typeof rawExpiresOn === 'number') {
      expiresOnMs = rawExpiresOn
    } else if (rawExpiresOn instanceof Date) {
      expiresOnMs = rawExpiresOn.getTime()
    } else if (typeof rawExpiresOn === 'string' && rawExpiresOn) {
      expiresOnMs = new Date(rawExpiresOn).getTime()
    }
    if (!accessToken || !Number.isFinite(expiresOnMs) || expiresOnMs <= Date.now()) return false

    try {
      window.sessionStorage.setItem(recoveryTimestampId, String(Date.now()))
    } catch (e) {
      // ignore
    }

    try {
      await this.syncMsalSession(accessToken, { updateUserInfo: false })
      return true
    } catch (e) {
      return false
    }
  }
 
  /**
   * Stores the newly acquired authentication result from MSAL.
   * Sets the cache expiration to equal the expiration time reduced by a fixed threshold
   * @param {AuthenticationResult} result
   */
  setMSALAuthResult(authenticationResult) {
    const expiresOn = parseIso8601Date(authenticationResult.expiresOn.toISOString());
    const msalTokenRefreshTime = new Date(expiresOn.getTime() - this.accessTokenValidityThresholdMs)
    this.cacheSet('msalLastAuthResult', {
        ...authenticationResult,
        expiresOn: expiresOn.getTime(),
        msalTokenRefreshTime: msalTokenRefreshTime.getTime()
      },
      expiresOn.getTime() - Date.now())
  }

  static persistMsalAccount(account) {
    if (
      typeof window === 'undefined' ||
      !account
    ) {
      return;
    }

    try {
      window.localStorage.setItem(
        'msal.account',
        JSON.stringify({
          homeAccountId: account.homeAccountId,
          username: account.username
        })
      );
    } catch (e) {
      // ignore
    }
  }

  isInteractionInProgressError(e) {
    return (
      String(e?.errorCode || '').includes('interaction_in_progress') ||
      String(e?.message || '').includes('interaction_in_progress') ||
      String(e?.message || '').includes('msal_interaction_in_progress')
    );
  }

  async waitForRedirectSync() {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      // eslint-disable-next-line no-underscore-dangle
      const redirectSyncPromise = window.__MSAL_REDIRECT_SYNC_PROMISE__;

      if (
        redirectSyncPromise &&
        typeof redirectSyncPromise.then === 'function'
      ) {
        await Promise.race([
          redirectSyncPromise,
          new Promise(resolve => {
            setTimeout(resolve, 6000);
          })
        ]);
      } else {
        await new Promise(resolve => {
          setTimeout(resolve, 600);
        });
      }
    } catch (e) {
      // ignore
    }
  }

  async handleInteractionInProgressError(self) {
    await this.waitForRedirectSync();

    const cachedAfterWait = this.getMSALAuthResult();

    if (cachedAfterWait?.accessToken) {
      return cachedAfterWait.accessToken;
    }

    const accountAfterWait =
      self.msal.instance.getActiveAccount() ||
      (self.msal.instance.getAllAccounts().length
        ? self.msal.instance.getAllAccounts()[0]
        : null);

    if (!accountAfterWait) {
      throw new Error('msal_interaction_in_progress');
    }

    try {
      const retryResult =
        await self.msal.instance.acquireTokenSilent({
          ...silentRequest,
          account: accountAfterWait,
          forceRefresh: false
        });

      if (retryResult?.accessToken) {
        self.setMSALAuthResult(retryResult);

        await self.syncMsalSession(
          retryResult.accessToken,
          { updateUserInfo: true }
        );

        return retryResult.accessToken;
      }
    } catch (e) {
      // ignore
    }

    throw new Error('msal_interaction_in_progress');
  }

  handleStateNotFoundError(e) {
    if (
      String(e?.message || '').includes('state_not_found')
    ) {
      Auth.clearMsalInteractionLock();
    }
  }

  getConcurrentRefreshedToken() {
    if (typeof window === 'undefined') {
      return null;
    }

    try {
      const MSAL_CACHE = 'auth.msalLastAuthResult';

      const cacheStr =
        window.sessionStorage.getItem(MSAL_CACHE) ||
        window.localStorage.getItem(MSAL_CACHE);

      if (!cacheStr) {
        return null;
      }

      const cached = JSON.parse(cacheStr);

      const expiresOn =
        cached?.expiresOn ||
        cached?.value?.expiresOn;

      const accessToken =
        cached?.value?.accessToken ||
        cached?.accessToken;

      if (
        accessToken &&
        expiresOn &&
        expiresOn > Date.now()
      ) {
        return accessToken;
      }
    } catch (e) {
      // ignore
    }

    return null;
  }

  async handleRedirectToSSO(e) {
    const sessionParams = this.getSessionParams();

    const customerContext = {
      selectedCustomerId:
        sessionParams?.custId ||
        sessionParams?.selectedCustomerId ||
        null,
      custId: sessionParams?.custId || null,
      selectedCustomerData:
        sessionParams?.selectedCustomerData || null
    };

    this.logError?.logErrorToServer?.({
      message:
        `MSAL token refresh failed - redirecting to SSO login. errorCode: ${
          e?.errorCode || e?.error || 'N/A'
        }, subError: ${
          e?.subError || e?.suberror || 'N/A'
        }, message: ${
          e?.message ||
          e?.error_description ||
          'unknown'
        }`,
      stackTrace: e?.stack || e?.error_description,
      component: 'Auth - getMsalAccessToken',
      type: 'error',
      statusCode: 401
    });

    const skipReturnUrl =
      isMsalTokenExpiredError(e);

    await this.redirectToSSOLogin({
      customerContext,
      skipReturnUrl
    });
  }

  async handleAuthPageMsalError(e, self) {
    const needsFreshSSOLogin =
      isMsalTokenExpiredError(e);

    if (!needsFreshSSOLogin) {
      throw e;
    }

    this.logError?.logErrorToServer?.({
      message:
        `MSAL token expired/invalid_grant on auth page - forcing fresh login. errorCode: ${
          e?.errorCode || e?.error || 'N/A'
        }, subError: ${
          e?.subError || e?.suberror || 'N/A'
        }`,
      stackTrace: e?.stack || e?.error_description,
      component: 'Auth - getMsalAccessToken',
      type: 'error',
      statusCode: 401
    });

    const isUnrecoverable =
      isUnrecoverableMsalSessionError(e);

    const interactionLock =
      Auth.getMsalInteractionLock();

    if (interactionLock) {
      const isPreviousExpiredTokenLock =
        interactionLock.reason?.includes(
          'expiredToken'
        );

      if (!isPreviousExpiredTokenLock) {
        return undefined;
      }

      Auth.clearMsalInteractionLock();
    }

    this.cache.removeItem(
      `${this.cacheKey}.msalLastAuthResult`
    );

    if (isUnrecoverable) {
      clearMsalStorage();

      if (typeof window !== 'undefined') {
        window.localStorage.removeItem(
          'msal.account'
        );

        window.sessionStorage.removeItem(
          'msal.account'
        );

        window.sessionStorage.removeItem(
          'auth.session.recovery.attempted.ts'
        );

        window.sessionStorage.removeItem(
          AUTH_REDIRECT_SYNC_TS
        );
      }
    }

    Auth.setMsalInteractionLock(
      'getMsalAccessToken.acquireTokenRedirect.expiredToken'
    );

    if (isUnrecoverable) {
      await self.msal.instance.loginRedirect({
        ...loginRequest,
        prompt: 'login'
      });
    } else {
      await self.msal.instance.acquireTokenRedirect(
        loginRequest
      );
    }

    return undefined;
  }

  logMsalAccessTokenError(e) {
    this.logError?.logErrorToServer?.({
      message:
        `getMsalAccessToken failed: ${
          e?.errorCode ||
          e?.error ||
          e?.message ||
          'unknown'
        }, subError: ${
          e?.subError ||
          e?.suberror ||
          'N/A'
        }`,
      stackTrace:
        e?.stack ||
        e?.error_description,
      component: 'Auth - getMsalAccessToken',
      type: 'error',
      statusCode: e?.status || 500
    });
  }
 
  async getMsalAccessToken() {
    if (this.msal?.instance?.initialize && this.msal.instance.initialized === false) {
      try {
        await this.msal.instance.initialize();
      } catch (initError) {
        //getMsalAccessToken: Failed to initialize MSAL instance
      }
    }

    let msalPendingAccessToken = Auth.getPendingMsalAccessToken()
    if (msalPendingAccessToken) {
      return msalPendingAccessToken
    }

    // Check if cached token is about to expire and needs proactive refresh
    const cachedAuthResult = this.getMSALAuthResult()
    const now = Date.now()
    if (cachedAuthResult?.msalTokenRefreshTime) {
      const timeUntilRefresh = cachedAuthResult.msalTokenRefreshTime - now
      if (timeUntilRefresh > 0) {
        return cachedAuthResult.accessToken
      } 
    }

    const self = this
    msalPendingAccessToken = Promise.resolve()
      .then(async () => {
        // If applications.js is already handling redirect flow, wait for it first.
        if (typeof window !== 'undefined') {
          // eslint-disable-next-line no-underscore-dangle
          const redirectSyncPromise = window.__MSAL_REDIRECT_SYNC_PROMISE__
          if (redirectSyncPromise && typeof redirectSyncPromise.then === 'function') {
            try {
              // 🔧 FIX: Always wait for redirect sync promise to settle, whether in-progress
              // or already completed. Previously only waited when NOT in progress, which caused
              // a race where getMsalAccessToken ran before setActiveAccount() completed and got
              // "no MSAL account found" immediately after a successful SSO redirect.
              await Promise.race([
                redirectSyncPromise,
                new Promise(resolve => {
                  setTimeout(resolve, 8000)
                })
              ])
            } catch (e) {
              //getMsalAccessToken: waiting for global redirect sync failed/timed out
            }
          }

          // After waiting for redirect sync to complete, return the redirect result if available.
          // eslint-disable-next-line no-underscore-dangle
          if (window.__MSAL_LAST_REDIRECT_RESULT__) {
            // eslint-disable-next-line no-underscore-dangle
            return window.__MSAL_LAST_REDIRECT_RESULT__
          }
        }

        // eslint-disable-next-line no-underscore-dangle
        if (typeof window !== 'undefined' && window.__MSAL_REDIRECT_HANDLED__ === true) {
          // eslint-disable-next-line no-underscore-dangle
          return window.__MSAL_LAST_REDIRECT_RESULT__ || null
        }

        if (typeof window !== 'undefined') {
          const hash = window.location.hash || ''
          const search = window.location.search || ''
          const hasRedirectResponse =
            hash.includes('code=')
            || hash.includes('id_token=')
            || hash.includes('access_token=')
            || (hash.includes('state=') && hash.includes('session_state='))
            || search.includes('code=')
            || search.includes('id_token=')
            || search.includes('access_token=')

          if (!hasRedirectResponse) {
            return null
          }
        }

        return self.msal.instance.handleRedirectPromise()
      })
      .then(async (redirectResult) => {
        if (redirectResult) {
          Auth.clearMsalInteractionLock()

          if (redirectResult.account && self.msal.instance.setActiveAccount) {
            self.msal.instance.setActiveAccount(redirectResult.account)
            self.msal.instance.enableAccountStorageEvents?.()
            Auth.persistMsalAccount(redirectResult.account);
          }
          return redirectResult
        }
        let account = self.msal.instance.getActiveAccount()
        if (!account) {
          let firstAccount = self.msal.instance.getAllAccounts().length ? self.msal.instance.getAllAccounts()[0] : null
          
          // CRITICAL: If no accounts found, try to restore from localStorage (after SSO redirect)
          if (!firstAccount && typeof window !== 'undefined') {
            try {
              const savedAccountJson = window.localStorage.getItem('msal.account');
              if (savedAccountJson) {
                const savedAccount = JSON.parse(savedAccountJson);
                
                // Retry loop: MSAL reconstructs its in-memory cache from localStorage
                // asynchronously after a page load. 100ms was too short; retry up to
                // 5 times with 200ms gaps (max 1s total) before giving up.
                const maxRetries = 5;
                const retryDelayMs = 200;
                for (let attempt = 1; attempt <= maxRetries; attempt += 1) {
                  // eslint-disable-next-line no-await-in-loop
                  await new Promise(resolve => { setTimeout(resolve, retryDelayMs); });
                  const retryAccounts = self.msal.instance.getAllAccounts();
                  const matchingAccount = retryAccounts.find(acc =>
                    acc.homeAccountId === savedAccount.homeAccountId ||
                    acc.username === savedAccount.username
                  );
                  if (matchingAccount) {
                    firstAccount = matchingAccount;
                    break;
                  }
                }
              }
            } catch (restoreError) {
              //getMsalAccessToken: Could not restore account from localStorage:
            }
          }
          
          if (!firstAccount) {
            const interactionLock = Auth.getMsalInteractionLock()
            if (interactionLock) {
              throw new Error('msal_interaction_in_progress')
            }

            Auth.setMsalInteractionLock('getMsalAccessToken.acquireTokenRedirect')
            await self.msal.instance.acquireTokenRedirect(loginRequest)
            throw new Error('should never reach here')
          }
          account = firstAccount
          self.msal.instance.setActiveAccount(firstAccount)
          self.msal.instance.enableAccountStorageEvents()
        }
        // Force refresh if cached token is expired/expiring, otherwise use silent refresh
        const shouldForceRefresh = !cachedAuthResult?.msalTokenRefreshTime || (cachedAuthResult.msalTokenRefreshTime - Date.now()) <= 0

        return self.msal.instance.acquireTokenSilent({
          ...silentRequest,
          account,
          forceRefresh: shouldForceRefresh
        })
      })
      .then(async (result) => {
        const lastAuthenticationResult = this.getMSALAuthResult()
        const expectedRefreshTime = new Date(result.expiresOn.getTime() - this.accessTokenValidityThresholdMs)

        this.logError?.logErrorToServer?.({
          message: `MSAL access token ${result.accessToken === lastAuthenticationResult?.accessToken ? 'reused' : 'refreshed'}. Expected refresh at: ${expectedRefreshTime.toISOString()}.`,
          stackTrace: undefined,
          component: 'Auth - getMsalAccessToken',
          type: 'info',
          statusCode: 200
        });

        if (result.accessToken !== lastAuthenticationResult?.accessToken) {
          self.setMSALAuthResult(result);

          await self.syncMsalSession(
            result.accessToken,
            { updateUserInfo: true }
          );

          Auth.persistMsalAccount(result.account);
        }
        return result.accessToken
      })
      .catch(async (e) => {
        this.logMsalAccessTokenError(e);

        if (this.isInteractionInProgressError(e)) {
          return this.handleInteractionInProgressError(self);
        }

        this.handleStateNotFoundError(e);

        if (e?.message === 'msal_interaction_in_progress') {
          throw e;
        }

        const isAlreadyOnAuthPage =
          typeof window !== 'undefined' &&
          window.location.pathname.includes('/auth/');

        if (isAlreadyOnAuthPage) {
          return this.handleAuthPageMsalError(e, self);
        }

        const cachedToken = this.getConcurrentRefreshedToken();

        if (cachedToken) {
          return cachedToken;
        }

        await this.handleRedirectToSSO(e);

        throw new Error(
          'should not reach here - redirected to SSO login'
        );
      })
      .finally(() => {
        Auth.setPendingMsalAccessToken(undefined)
      })
    Auth.setPendingMsalAccessToken(msalPendingAccessToken)
    return msalPendingAccessToken
  }
 
  async setUserInfo(userInfo) {
    this.setSessionParams({
      id: userInfo.id,
      type: 'USER',
      roles: userInfo.roles,
      firstName: userInfo.firstName,
      lastName: userInfo.lastName,
      phone: userInfo.phone,
      username: userInfo.username,
      email: userInfo.email,
      isInternal: userInfo.roles.includes('b2binternal'),
      localeInfo: userInfo.localeInfo,
      organization: userInfo.organization
    })
    // 🔧 CRITICAL: Also persist to localStorage for cross-module access (proactive token refresh, etc.)
    // This ensures sessionContext is available even across page loads or multiple modules
    if (typeof window !== 'undefined') {
      try {
        const sessionData = {
          id: userInfo.id,
          email: userInfo.email,
          username: userInfo.username,
          firstName: userInfo.firstName,
          lastName: userInfo.lastName,
          phone: userInfo.phone,
          roles: userInfo.roles,
          isInternal: userInfo.roles.includes('b2binternal'),
          localeInfo: userInfo.localeInfo,
          organization: userInfo.organization,
          userEmail: userInfo.email, // For backwards compatibility
          type: 'USER'
        };
        window.localStorage.setItem('sessionContext', JSON.stringify(sessionData));
      } catch (storageError) {
        //Could not persist sessionContext to localStorage:
      }
    }
  }
 
  async gqlAuthCallbackMSAL(accessToken) {
    return this.getApolloClient().mutate({
      mutation: this.GQL_AUTH_CALLBACK,
      variables: {
        code: accessToken,
        isInternal: true,
        isLocal: this.isLocal,
        source: 'castrolmarine',
        codeVerifier: this.getSessionParams().codeVerifier
      },
      context: {
        skipAzureTokenCheck: true,
        shouldFailUponAuthFailure: true,
        shouldAlwaysFailAsAuthError: true
      }
    }).then((authCallbackResult) => {
      invariant(authCallbackResult?.data?.authCallback?.wasSuccessful, `failed graphql auth callback: ${JSON.stringify(authCallbackResult)}`)
      return authCallbackResult.data.authCallback
    })
  }

  async refreshMsalToken() {
    return this.getMsalAccessToken(true)
  }

  async syncMsalSession(accessToken, options = {}) {
    const { updateUserInfo = true } = options
    await this.gqlAuthCallbackMSAL(accessToken)

    if (!updateUserInfo) {
      return
    }

    try {
      const userInfo = await this.gqlUserInfo(true)
      await this.setUserInfo(userInfo)
    } catch (userInfoError) {
      try {
        const userInfo = await this.gqlUserInfo(false)
        await this.setUserInfo(userInfo)
      } catch (fallbackUserInfoError) {
        trackError('syncMsalSession_userInfoError', fallbackUserInfoError.message)
      }
    }
  }
 
  async gqlAuthCallbackCIP(code) {
    return this.getApolloClient().mutate({
      mutation: this.GQL_AUTH_CALLBACK,
      variables: {
        code,
        isInternal: false,
        isLocal: this.isLocal,
        source: 'castrolmarine',
        codeVerifier: this.getSessionParams().codeVerifier
      },
      context: {
        shouldFailUponAuthFailure: true,
        shouldAlwaysFailAsAuthError: true
      }
    }).then((authCallbackResult) => {
      invariant(authCallbackResult?.data?.authCallback?.wasSuccessful, `failed graphql auth callback: ${JSON.stringify(authCallbackResult)}`)
      return authCallbackResult?.data?.authCallback
    }).catch((e) => {
      throw e
    })
  }
 
  async gqlUserInfo(skipAzureTokenCheck) {
    return this.getApolloClient().query({
      query: this.GQL_ME,
      fetchPolicy: 'no-cache',
      context: {
        skipAzureTokenCheck,
        shouldFailUponAuthFailure: skipAzureTokenCheck,
        shouldAlwaysFailAsAuthError: true
      }
    }).then((meResult) => {
      invariant(meResult?.data?.me?.roles, `failed graphql auth callback: ${JSON.stringify(meResult)}`)
      return meResult.data.me
    }).catch((e) => {
      throw e
    })
  }

  /**
   * Restores user to previously visited page after SSO login (instance method)
   * Should be called from the /auth/employee callback or success page
   */
  restorePostLoginState() {
    try {
      // Use static method to get redirect state
      const redirectState = Auth.getRedirectState()
      if (redirectState?.returnUrl) {// CRITICAL FIX: Don't redirect back to auth pages themselves (prevents loops)
        // Auth pages should handle their own navigation flow
        const authPagePaths = ['/auth/employee', '/auth/callback', '/auth/landing', '/auth/asmUser', '/auth/asmLogin'];
        const isAuthPage = authPagePaths.some(path => redirectState.returnUrl.includes(path));
        
        if (isAuthPage) {
          Auth.clearRedirectState();
          return false;
        }
        
        // Clear the state first using static method
        Auth.clearRedirectState()
        
        // If there's customer context, restore it to session
        if (redirectState.customerContext?.selectedCustomerId) {
          const currentSession = this.getSessionParams()
          const restoredSession = {
            ...currentSession,
            custId: redirectState.customerContext.custId || redirectState.customerContext.selectedCustomerId,
            selectedCustomerId: redirectState.customerContext.selectedCustomerId,
            selectedCustomerData: redirectState.customerContext.selectedCustomerData,
            userInfo: {
              ...(currentSession?.userInfo || {}),
              isASM: true
            }
          }
          try {
            const existing = sessionContextStorage.get() || {}
            sessionContextStorage.set({ ...existing, ...restoredSession })
          } catch (storageErr) {
            this.logError?.logErrorToServer?.({
              message: `restorePostLoginState: failed to persist session to storage: ${storageErr?.message || 'unknown'}`,
              stackTrace: storageErr?.stack,
              component: 'Auth - restorePostLoginState',
              type: 'warn',
              statusCode: 500
            })
          }
          this.setSessionParams(restoredSession)
        }
        
        if (typeof window !== 'undefined') {
          try {
            const parsedReturnUrl = new URL(redirectState.returnUrl, window.location.origin);
            if (!['http:', 'https:'].includes(parsedReturnUrl.protocol)) {
              throw new Error('Invalid protocol');
            }
            if (parsedReturnUrl.origin !== window.location.origin) {
              throw new Error('Cross-origin redirect blocked');
            }
            const safePath = `${parsedReturnUrl.pathname}${parsedReturnUrl.search}${parsedReturnUrl.hash}`;
            if (!/^\/[\w\-./~!$&'()*+,;=:@%?#[\]]*$/.test(safePath)) {
              throw new Error('Invalid redirect path');
            }
            window.location.assign(safePath);
          } catch (e) {
            this.logError?.logErrorToServer?.({
              message: `restorePostLoginState: failed to redirect - invalid URL: ${e.message}`,
              stackTrace: e?.stack,
              component: 'Auth - restorePostLoginState',
              type: 'error',
              statusCode: 400
            });
          }
        }
        return true
      }
    } catch (e) {
      //Failed to restore post-login state
    }
    return false
  }
}